import styles from 'styles/views/Highlighter.css';
import React, { Component, PropTypes } from 'react';
import { MODE } from 'constants/mode';
import { SELECTION_TYPE } from 'model/Event';
import { isFixed } from 'utils/css';

export default class Highlighter extends Component {
  static propTypes = {
    editorState: PropTypes.object,
    events: PropTypes.array,
    focusedEvent: PropTypes.object,
    hoveredElement: PropTypes.object,
    elementLabel: PropTypes.object
  };

  render () {
    if (this.props.editorState.mode === MODE.BROWSE) {
      return null;
    }
    let hoveredElementLabel = null;
    let hoveredEvents = [];
    if (this.props.focusedEvent) {
      hoveredEvents = this.props.events.filter((event) => {
        return event.elements.includes(this.props.hoveredElement);
      });
    }
    if (hoveredEvents.length > 0) {
      hoveredElementLabel = hoveredEvents.length > 1
        ? `${hoveredEvents.length} events` : hoveredEvents[0].name;
    }

    let eventElements = this.getEventElements(this.props.events.filter((event) => {
      // focused event will be handled separately
      if (this.props.focusedEvent && event.id === this.props.focusedEvent.id) {
        return false;
      }
      return true;
    }));

    let focusedElements = this.getEventElements(this.props.focusedEvent ? [this.props.focusedEvent] : []);

    const isIframe = this.props.hoveredElement && this.props.hoveredElement.tagName &&
    this.props.hoveredElement.tagName.toLowerCase() === 'iframe';
    let showHoveredElement = this.props.hoveredElement && !isIframe &&
      focusedElements.indexOf(this.props.hoveredElement) < 0;

    // if selection type is "manual," highlight already selected element in red color for toggle off
    let hoveredElementStyle = styles.focus;
    if (this.props.focusedEvent && this.props.focusedEvent.selectionType === SELECTION_TYPE.MANUAL) {
      let hoveredIndex = focusedElements.indexOf(this.props.hoveredElement);
      if (focusedElements && hoveredIndex >= 0) {
        let originalElement = this.props.focusedEvent.originalElement;
        if (originalElement !== this.props.hoveredElement && !this.props.elementLabel) {
          hoveredElementStyle = styles.delete;
          focusedElements.splice(hoveredIndex, 1);
          showHoveredElement = true;
        }
      }
    }

    return (
      <div className={styles.container}>
        {this.highlightElements(eventElements, styles.highlight)}
        {this.highlightElements(focusedElements, styles.edit)}
        {
          hoveredElementLabel &&
            this.showHoveredElementLabel(hoveredElementLabel)
        }
        {
          showHoveredElement &&
            <div
              className={hoveredElementStyle}
              style={this.getBoundingBox(this.props.hoveredElement)}/>
        }
      </div>
    );
  };

  getBoundingBox = (el) => {
    const bb = {
      height: 0,
      left: 0,
      top: 0,
      width: 0
    };
    const BORDER_WIDTH = 2;
    if (el) {
      const bounds = this.getElementBounds(el);
      Object.assign(bb, {
        height: bounds.bottom - bounds.top + (BORDER_WIDTH * 2) + 'px',
        left: (bounds.left + (window.scrollX || window.pageXOffset) - BORDER_WIDTH) + 'px',
        top: (bounds.top + (window.scrollY || window.pageYOffset) - BORDER_WIDTH) + 'px',
        width: bounds.right - bounds.left + (BORDER_WIDTH * 2) + 'px'
      });
      if (isFixed(el)) {
        bb.position = 'fixed !important';
      }
    }
    return bb;
  };

  getElementBounds (el) {
    let bounds;

    // a lot of people use anchor tags to wrap children and make them all linkable
    // (for an example look at the video tiles on youtube.com home page)
    // since anchor tags are display inline by default, we want to calculate the bounds
    // of all of its children. however, we only want to do that when all of the direct
    // children are element nodes (and not text nodes + other children like we do often
    // for icons + text)
    const allChildrenAreElements = el.children.length === el.childNodes.length;
    if (el.tagName.toLowerCase() === 'a' && el.children.length > 0 && allChildrenAreElements) {
      Array.from(el.children)
        .filter(child => { // filter out elements that are hidden or inline
          const styles = getComputedStyle(child);
          return !(child.tagName === 'a' || styles.display === 'none');
        })
        .forEach(child => {
          const b = child.getBoundingClientRect();
          if (!bounds) {
            bounds = {
              top: b.top,
              right: b.right,
              bottom: b.bottom,
              left: b.left
            };
          }
          if (b.top < bounds.top) {
            bounds.top = b.top;
          }
          if (b.right > bounds.right) {
            bounds.right = b.right;
          }
          if (b.bottom > bounds.bottom) {
            bounds.bottom = b.bottom;
          }
          if (b.left < bounds.left) {
            bounds.left = b.left;
          }
        });
    } else {
      bounds = el.getBoundingClientRect();
    }

    if (!bounds || (!bounds.height && !bounds.width)) {
      if (this.props.focusedEvent &&
        el === this.props.focusedEvent.originalElement) {
        // in case the element become hidden, use the initially captured rect
        bounds = {...this.props.focusedEvent.originalRect};
        let scrollXDelta = bounds.scrollX - (window.scrollX || window.pageXOffset);
        let scrollYDelta = bounds.scrollY - (window.scrollY || window.pageYOffset);
        bounds.top += scrollYDelta;
        bounds.bottom += scrollYDelta;
        bounds.left += scrollXDelta;
        bounds.right += scrollXDelta;
      }
    }

    if (!bounds) {
      bounds = {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      };
    }

    return bounds;
  };

  getEventElements (events) {
    let elements = [];
    events && events.forEach((event) => {
      elements.push(...event.elements);
    });
    return elements;
  };

  getLabelPosition (el, label) {
    let host = document.body;
    let sizer = document.createElement('div');
    sizer.className = styles.label;
    sizer.style.visibility = 'hidden';
    sizer.style.display = 'table';
    sizer.innerHTML = label;
    host.appendChild(sizer);
    const labelBounds = sizer.getBoundingClientRect();
    const labelWidth = labelBounds.right - labelBounds.left;
    host.removeChild(sizer);

    let pos = {};
    const elBounds = this.getElementBounds(el);
    const elWidth = elBounds.right - elBounds.left;
    const left = elBounds.left + (elWidth / 2) - (labelWidth / 2) + (window.scrollX || window.pageXOffset) + 'px';
    pos = {
      left: left,
      top: elBounds.top + window.scrollY + 'px'
    };
    return pos;
  };

  highlightElements (elements, className) {
    return elements.map((element, index) => {
      if (!isElementHidden(element)) {
        return (
          <div
            key={index}
            className={className}
            style={this.getBoundingBox(element)}
            >
            {' '}
          </div>
        );
      }
    });
  };

  showHoveredElementLabel (label) {
    return (
      <div
        className={styles.label}
        style={this.getLabelPosition(this.props.hoveredElement, label)}>
        {label}
      </div>
    );
  }
}

function isElementHidden (element) {
  const elem = window.getComputedStyle(element);
  if (elem.opacity === '0' || elem.display === 'none') {
    return true;
  }

  let opacity = 1;
  while (element && element.nodeType === 1) {
    const op = getComputedStyle(element);
    if (opacity === 0) {
      return true;
    }
    if (op) {
      opacity *= parseFloat(op.opacity);
    }
    element = element.parentNode;
  }
  return false;
}



/** WEBPACK FOOTER **
 ** ./src/views/Highlighter.js
 **/